import { ReactNode } from 'react'
import { ExpandMore } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Link,
  Typography,
} from '@mui/material'
import { JourneySheet } from '@/components/credit-journey/journey-sheet'

type ComboDetailsProps = {
  open: boolean
  onClose: () => void
  rate: string
  installment: string
  accounting: string
  total: string
  months: number
}

const green = (text: string) => (
  <Box component="b" sx={{ color: 'creditJourney.gain' }}>
    {text}
  </Box>
)

function Row({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 1.5,
        py: 1.25,
        borderTop: 1,
        borderColor: 'divider',
        fontSize: 14.5,
      }}
    >
      <span>{label}</span>
      <span>{children}</span>
    </Box>
  )
}

function Question({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography fontSize={14} fontWeight={500}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          display: 'grid',
          gap: 1.5,
          fontSize: 14,
          '& p': { m: 0 },
          '& ul': { m: 0, pl: 2.5, display: 'grid', gap: 0.75 },
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  )
}

export function ComboDetails({
  open,
  onClose,
  rate,
  installment,
  accounting,
  total,
  months,
}: ComboDetailsProps) {
  const during = (
    <>
      {' '}
      / mês, durante {green(`${months} meses`)}
    </>
  )
  return (
    <JourneySheet open={open} title="Resumo da contratação" onClose={onClose}>
      <Box>
        <Row label="Taxa de juros">
          {green(rate)} / mês
        </Row>
        <Row label="Abertura de CNPJ (valor de mercado: R$1.200)">{green('Grátis')}</Row>
        <Row
          label={
            <>
              {green('(A)')}
              <br />
              Parcela Mesada
            </>
          }
        >
          {green(installment)}
          {during}
        </Row>
        <Row
          label={
            <>
              {green('(B)')}
              <br />
              Contabilidade Médica completa com 50% de desconto
            </>
          }
        >
          {green(accounting)}
          {during}
        </Row>
        <Row
          label={
            <>
              {green('(A + B)')}
              <br />
              Valor mensal total
            </>
          }
        >
          {green(total)}
          {during}
        </Row>
      </Box>

      <Typography fontWeight={500} mt={1}>
        Dúvidas Frequentes
      </Typography>

      <Question title="Por que a Alume oferece Contabilidade Médica?">
        <p>
          A Alume Mesada existe para que você possa se preocupar menos com as finanças e{' '}
          <b>dedicar sua atenção ao que importa:</b> seus estudos, para ser um(a) médico(a) cada
          vez melhor.
        </p>
        <p>
          Você vai passar por um desafio de foco parecido quando for começar a trabalhar: na
          grande maioria dos hospitais, para receber seus plantões você vai precisar...{' '}
          <b>ter uma empresa própria</b>! Que é a tal da PJ Médica.
        </p>
        <p>
          Para abrir e manter uma empresa, mesmo que ela seja só você mesmo(a), você
          necessariamente vai precisar de um contador. Opções no mercado não faltam, mas pensa
          aqui:
        </p>
        <ul>
          <li>
            Muitos vão <b>delegar para você o trabalho de emitir Notas Fiscais.</b> Você quer
            gastar tempo com isso, ou atendendo pacientes?
          </li>
          <li>
            Outros vão gerenciar sua empresa sem conhecimento das particularidades da medicina,
            fazendo com que você <b>pague mais impostos do que o necessário.</b>
          </li>
          <li>
            Sabe o pior? Você só vai saber dos problemas quando a bomba já tiver estourado - e
            chegarem <b>as multas.</b>
          </li>
        </ul>
        <p>
          Aqui na Alume <b>acreditamos no seu potencial</b> e nos preocupamos com seu{' '}
          <b>sucesso financeiro</b>, por isso criamos um serviço de contabilidade para que você
          não perca dinheiro e/ou oportunidades de plantão por falta de suporte.
        </p>
      </Question>

      <Question title="Quais são os benefícios da Alume Contabilidade?">
        <p>
          Com a Alume, especialista em médicos e milhares de clientes em todo o Brasil, você vai{' '}
          <b>economizar o máximo de impostos e gastar o mínimo tempo possível com burocracia.</b>{' '}
          Aí sobra tempo para dedicar ao que mais importa: seus pacientes e sua carreira.
        </p>
        <p>
          Precisa emitir uma Nota Fiscal? É só mandar um WhatsApp e <b>a gente faz por você.</b>{' '}
          Tem dúvidas? <b>A qualidade e a velocidade do atendimento</b> são os pontos mais
          elogiados por quem já é Alume. Ah, e com <b>nosso aplicativo</b>, você pode acompanhar
          tudo da sua empresa na palma da mão, quando quiser.
        </p>
        <p>
          Contratando a Alume Contabilidade junto com a Mesada, você{' '}
          <b>ganha tempo e tranquilidade</b>. Não precisa mais se preocupar com nada: a abertura
          da sua PJ já estará programada para a data da sua formatura, para que você possa
          começar a trabalhar o mais rápido possível.
        </p>
        <p>
          “Ah, mas aumenta um pouco o valor da minha parcela depois da formatura”. Sim, mas na
          verdade, essa é a melhor parte. O preço da Contabilidade nesse combo tem um{' '}
          <b>desconto de 50%</b>, e é um plano de valor fixo: ou seja,{' '}
          <b>você pode faturar o quanto quiser</b> e vai pagar sempre a mesma coisa.
        </p>
        <p>É sério, pode pesquisar e comparar. Não existe nada parecido no mercado com esse valor.</p>
        <p>
          Bônus: No combo, <b>os juros da Mesada são mais baixos!</b>
        </p>
        <p>E só você, estudante Alume Mesada, tem acesso a essa condição exclusiva.</p>
      </Question>

      <Question title="Posso contratar a Mesada sem incluir a Contabilidade?">
        <p>Pode, sem quaisquer restrições, é só selecionar essa opção aqui no simulador.</p>
        <p>Sugerimos que pare um pouco e considere:</p>
        <ul>
          <li>
            Depois da formatura, você pode até tirar alguns dias para descansar, mas
            eventualmente vai querer trabalhar, certo?
          </li>
          <li>
            Hospitais, UPAs, ambulatórios e outros exigem que você tenha uma PJ para poder
            receber. O pagamento costuma ocorrer de 30 a 60 dias após a prestação do serviço. Em
            algumas regiões, mais.
          </li>
          <li>
            Pondere o “fator Brasil”: dependendo da cidade e da época do ano, o processo de
            abertura pode ser rápido ou <b>pode demorar meses</b>, mesmo fazendo tudo nos
            conformes. Não acredite em promessas falsas de outros contadores: Todos dependem dos
            mesmos serviços do governo.
          </li>
        </ul>
        <p>
          Ou seja, quando você quiser começar a ganhar dinheiro,{' '}
          <b>não ter uma PJ vai atrasar os seus recebimentos</b>. Isso sem nem contar que{' '}
          <b>oportunidades de trabalho podem ser perdidas</b> - de novo, porque a sua empresa
          (você) ainda não vai estar pronta para ser contratada.
        </p>
        <p>
          A Alume tem todo o interesse que você seja um(a) médico(a) bem-sucedido, já desde o
          começo da carreira, e ter uma boa contabilidade é fundamental. Acreditamos tanto nisso,
          que <b>os juros da Mesada</b> são mais baixos para quem já contratar a Contabilidade
          junto.
        </p>
      </Question>

      <Question title="Quanto custam os planos da Alume Contabilidade?">
        <p>A Alume Contabilidade oferece 3 planos:</p>
        <ul>
          <li>
            <b>Flex:</b> Paga-se por mês 3% do faturamento do mês anterior, ou o mínimo de
            R$179/mês. Ideal para quem não tem uma receita frequente e/ou previsível.
          </li>
          <li>
            <b>Fixo:</b> Como o nome diz, o valor é o mesmo todo mês, independentemente do
            faturamento. A mensalidade é de R$459/mês.
          </li>
          <li>
            <b>Premium:</b> Tudo do Fixo, em adição a Alume te dá o Certificado Digital eCNPJ e
            faz o Imposto de Renda Pessoa Física para você. A mensalidade é de R$509/mês.
          </li>
        </ul>
        <p>
          Todos incluem contabilidade completa, emissão de Notas Fiscais ilimitadas via WhatsApp,
          suporte no cadastro em hospitais, acesso ao app e muito mais.
        </p>
        <p>
          O plano que está incluso no combo com a Mesada é o <b>Fixo</b>, com 50% de desconto, ou
          seja, você pagará apenas {accounting}/mês. Uma <b>condição exclusiva</b> que só quem é
          Alume Mesada tem acesso.
        </p>
      </Question>

      <Question title="Posso trocar de plano?">
        <p>A troca de plano pode ser feita apenas após o prazo do contrato.</p>
        <p>
          É simples: Se por exemplo você receber a Mesada por 12 meses, irá pagar por 12 meses
          depois da formatura, certo? A possibilidade de troca de plano considera o mesmo prazo.
          Ou seja, nesse exemplo que acabamos de usar, você vai poder trocar de plano após 12
          meses.
        </p>
      </Question>

      <Question title="Posso cancelar o plano?">
        <p>
          O cancelamento de plano é possível, mas incorre em uma multa de R$1.500,00 se for feito
          antes do prazo do contrato.
        </p>
        <p>
          A multa existe para cobrir os custos que foram gerados ao abrir e manter uma empresa
          (pessoas, sistemas, taxas, etc.). Esses custos existem, mesmo se a empresa nunca for
          utilizada e/ou se nenhuma nota fiscal for emitida. Ela existe também para compensar,
          ainda que parcialmente, a redução dos juros da Mesada que foi concedida na contratação
          conjunta.
        </p>
        <p>
          Não se preocupe: Você obviamente vai ter a oportunidade de ler tudo isso no contrato
          antes de decidir se quer seguir adiante. E se tiver qualquer outra dúvida, nosso time
          está à disposição.
        </p>
      </Question>

      <Question title="Ainda tenho dúvidas. Tem alguém para me explicar melhor?">
        <p>
          Claro! Nosso time está pronto para te ajudar. Você pode falar com a gente via e-mail no{' '}
          <Link href="mailto:contato@alume.com">contato@alume.com</Link>, ou no WhatsApp - nosso
          horário de atendimento é de segunda a sexta-feira, das 09:00 às 18:00.
        </p>
        <p>
          <Link href="https://api.whatsapp.com/send?phone=5511937586934" target="_blank" rel="noopener">
            Atendimento Alume
          </Link>{' '}
          - Dúvidas em geral.
        </p>
        <p>
          <Link href="https://api.whatsapp.com/send?phone=5511942340808" target="_blank" rel="noopener">
            Atendimento Alume Contabilidade
          </Link>{' '}
          - Para dúvidas específicas.
        </p>
      </Question>

      <Button variant="text" onClick={onClose}>
        Fechar
      </Button>
    </JourneySheet>
  )
}
